import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const formUrl = 'https://readdy.ai/api/form/d9slrp94ne17ei0sdjjg';
    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim() }).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setSubscribeStatus('success');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setErrorMsg(responseText || '订阅失败，请稍后再试');
      }
    } catch {
      setSubscribeStatus('error');
      setErrorMsg('网络错误，请稍后再试');
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/70">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <span className="text-background-50 font-heading text-base font-bold">蜜</span>
              </div>
              <span className="font-heading text-lg text-foreground-900">蜜桃网</span>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              蜜桃网是值得信赖的内容聚合与资源发现平台，汇聚海量优质内容，提供便捷高效的一站式浏览体验。
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-300 transition-colors cursor-pointer">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-300 transition-colors cursor-pointer">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-300 transition-colors cursor-pointer">
                <i className="ri-bilibili-line"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">
              快速链接
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  关于我们
                </Link>
              </li>
              <li>
                <a href="#" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                  用户协议
                </a>
              </li>
              <li>
                <a href="#" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                  隐私政策
                </a>
              </li>
              <li>
                <a href="#" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                  免责声明
                </a>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">
              内容分类
            </h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">
              订阅资讯
            </h4>
            <p className="text-sm text-foreground-600 mb-4">
              定期获取精选内容和最新动态，不错过任何精彩。
            </p>
            {subscribeStatus === 'success' ? (
              <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 rounded-lg px-4 py-3">
                <i className="ri-checkbox-circle-line text-lg"></i>
                订阅成功！感谢您的关注。
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2" data-readdy-form="true">
                <div className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="请输入邮箱地址"
                    required
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    订阅
                  </button>
                </div>
                {subscribeStatus === 'error' && (
                  <p className="text-xs text-red-500">{errorMsg}</p>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; 2026 蜜桃网 MiTaoWang.com 版权所有
          </p>
          <p className="text-xs text-foreground-400">
            本站内容仅供学习交流，不构成任何建议
          </p>
        </div>
      </div>
    </footer>
  );
}